/**
 * <demand-notify-form> — back-in-stock signup form.
 *
 * Posts to the rarely-demand-api Vercel endpoint after running an invisible
 * Cloudflare Turnstile challenge. Endpoint, site key, and product context
 * are passed as data-* attributes on the element (see demand-soldout-form.liquid).
 *
 * Plain vanilla — no theme framework imports, intentionally matching the
 * inline JS pattern in templates/product.liquid.
 */
(function () {
  if (customElements.get('demand-notify-form')) return;

  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** @type {Promise<void> | null} */
  var turnstileReady = null;

  function loadTurnstile() {
    if (turnstileReady) return turnstileReady;
    turnstileReady = new Promise(function (resolve, reject) {
      if (window.turnstile) {
        resolve();
        return;
      }
      var existing = document.querySelector('script[data-demand-turnstile]');
      if (existing) {
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('turnstile_load_failed')); });
        return;
      }
      var s = document.createElement('script');
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.defer = true;
      s.setAttribute('data-demand-turnstile', '');
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('turnstile_load_failed')); };
      document.head.appendChild(s);
    });
    return turnstileReady;
  }

  class DemandNotifyForm extends HTMLElement {
    constructor() {
      super();
      this._abort = null;
      this._widgetId = null;
      this._submitting = false;
      this._token = '';
    }

    connectedCallback() {
      this._form = this.querySelector('form');
      this._input = this.querySelector('input[type="email"]');
      this._submit = this.querySelector('button[type="submit"]');
      this._status = this.querySelector('[data-status]');
      this._turnstileEl = this.querySelector('.cf-turnstile');
      if (!this._form || !this._input || !this._submit || !this._status || !this._turnstileEl) return;

      this._onSubmit = this._onSubmit.bind(this);
      this._form.addEventListener('submit', this._onSubmit);

      loadTurnstile().then(this._renderTurnstile.bind(this)).catch(function () { /* fall back to no-token submit; server rejects */ });
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._form && this._onSubmit) this._form.removeEventListener('submit', this._onSubmit);
      if (window.turnstile && this._widgetId !== null) {
        try { window.turnstile.remove(this._widgetId); } catch (_) {}
      }
    }

    _renderTurnstile() {
      if (!window.turnstile || this._widgetId !== null) return;
      var siteKey = this.dataset.turnstileSiteKey;
      var self = this;
      // Invisible mode: widget renders, runs the challenge silently in the
      // background, delivers a token via callback. We stash the token and
      // use it at submit time. After each submission we reset() to get a
      // fresh token (tokens are single-use).
      this._widgetId = window.turnstile.render(this._turnstileEl, {
        sitekey: siteKey,
        size: 'invisible',
        callback: function (token) { self._token = token; },
        'error-callback': function () { self._token = ''; },
        'expired-callback': function () { self._token = ''; },
        'timeout-callback': function () { self._token = ''; },
      });
    }

    _onSubmit(event) {
      event.preventDefault();
      if (this._submitting) return;

      var email = (this._input.value || '').trim();
      if (!email || !EMAIL_RE.test(email)) {
        this._showStatus('error', this._t('error_invalid_email'));
        this._input.focus();
        return;
      }

      this._clearStatus();
      this._setSubmitting(true);
      this._submitWithToken(this._token);
    }

    _submitWithToken(token) {
      var self = this;
      if (this._abort) this._abort.abort();
      this._abort = new AbortController();

      var body = {
        email: (this._input.value || '').trim(),
        product_id: Number(this.dataset.productId),
        product_handle: this.dataset.productHandle,
        product_title: this.dataset.productTitle,
        variant_id: this.dataset.variantId ? Number(this.dataset.variantId) : null,
        turnstile_token: token,
      };

      fetch(this.dataset.apiUrl, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: this._abort.signal,
      })
        .then(function (r) {
          return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, status: r.status, body: j }; });
        })
        .then(function (res) {
          self._setSubmitting(false);
          if (res.ok) {
            self._showSuccess(res.body && res.body.message);
          } else {
            self._showStatus('error', self._t('error_generic'));
          }
          self._resetTurnstile();
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          self._setSubmitting(false);
          self._showStatus('error', self._t('error_generic'));
          self._resetTurnstile();
        });
    }

    _showSuccess(serverMessage) {
      var msg = serverMessage || this._t('success_generic');
      this._showStatus('success', msg);
      this._form.querySelectorAll('input, button').forEach(function (el) { el.disabled = true; });
    }

    _showStatus(state, text) {
      this._status.textContent = text;
      this._status.dataset.state = state;
      this._status.hidden = false;
    }

    _clearStatus() {
      this._status.textContent = '';
      this._status.removeAttribute('data-state');
      this._status.hidden = true;
    }

    _setSubmitting(flag) {
      this._submitting = flag;
      this._submit.disabled = flag;
      this._submit.setAttribute('aria-busy', flag ? 'true' : 'false');
    }

    _resetTurnstile() {
      this._token = '';
      if (window.turnstile && this._widgetId !== null) {
        try { window.turnstile.reset(this._widgetId); } catch (_) {}
      }
    }

    _t(key) {
      var map = {
        success_generic: this.dataset.textSuccess || "You're on the list. We'll email you when it's back.",
        error_generic: this.dataset.textError || 'Something went wrong. Please try again.',
        error_invalid_email: this.dataset.textInvalidEmail || 'Please enter a valid email address.',
      };
      return map[key] || '';
    }
  }

  customElements.define('demand-notify-form', DemandNotifyForm);
})();
